/**
 * Service pour les parcours personnalisés
 */

import api from './api.js';
import { isNetworkError } from '../utils/networkError.js';
import {
  cacheCasTemplate,
  getCachedCasTemplate,
  CACHEABLE_CAS_CODES,
} from '../offline/templateCache.js';

/**
 * Obtenir le parcours d'un bénéficiaire
 */
export async function obtenirParcours(beneficiaireId) {
  const response = await api.get(`/parcours/${beneficiaireId}`);
  return response.data;
}

/**
 * Squelette étapes pour un CAS (sans bénéficiaire) — aligné sur PARCOURS_PAR_CAS backend.
 * @param {string} casNumero ex. CAS-A, CAS-H
 */
export async function getParcoursCasTemplate(casNumero) {
  const enc = encodeURIComponent(String(casNumero || '').trim());
  try {
    const response = await api.get(`/parcours/cas/${enc}/template`);
    // Write-through so the same template is available offline later (fire-and-forget).
    cacheCasTemplate(casNumero, response.data).catch(() => {});
    return response.data;
  } catch (err) {
    // Offline / unreachable: fall back to the cached template if we have it.
    if (isNetworkError(err)) {
      const cached = await getCachedCasTemplate(casNumero);
      if (cached) return cached;
    }
    throw err;
  }
}

/**
 * Warm the offline cache with every document-parcours CAS template, so the
 * questionnaire-completion preview works in the field even for a CAS the Relais
 * hasn't opened online yet. Best-effort: failures are swallowed per template.
 */
export async function prefetchCasTemplates() {
  await Promise.all(
    CACHEABLE_CAS_CODES.map((code) => getParcoursCasTemplate(code).catch(() => {})),
  );
}

/**
 * Générer et sauvegarder le parcours pour un bénéficiaire
 */
export async function genererParcours(beneficiaireId) {
  const response = await api.post(`/parcours/${beneficiaireId}/generer`);
  return response.data;
}

/**
 * Mettre à jour le statut d'une étape et/ou le statut document
 * @param {string} etapeId
 * @param {string|{ statut?: string, document_statut?: 'needed'|'requested'|'obtained', dependency_status?: object, localisation?: string }} statutOrPayload - statut (step) or full payload
 * @param {{ signal?: AbortSignal }} options - optional abort signal for cancellation
 */
export async function mettreAJourEtape(etapeId, statutOrPayload, options = {}) {
  const body = typeof statutOrPayload === 'string'
    ? { statut: statutOrPayload }
    : { ...statutOrPayload };
  const config = options.signal ? { signal: options.signal } : {};
  const response = await api.put(`/parcours/etapes/${etapeId}`, body, config);
  return response.data;
}

const RETRY_DELAYS_MS = [500, 1000];

function isRetryableError(err) {
  const status = err.response?.status;
  if (status != null && status >= 500) return true;
  if (status == null && err.message) {
    const msg = err.message.toLowerCase();
    if (msg.includes('network') || msg.includes('econnreset') || msg.includes('timeout') || msg.includes('failed')) return true;
  }
  return false;
}

/**
 * Same as mettreAJourEtape but retries on 5xx and network errors (transient failures).
 * @param {string} etapeId
 * @param {string|object} statutOrPayload
 * @param {{ signal?: AbortSignal, maxRetries?: number }} options - maxRetries = number of retries after first attempt (default 2 => 3 attempts total)
 */
export async function mettreAJourEtapeWithRetry(etapeId, statutOrPayload, options = {}) {
  const { signal, maxRetries = 2 } = options;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await mettreAJourEtape(etapeId, statutOrPayload, { signal });
    } catch (err) {
      if (attempt < maxRetries && isRetryableError(err)) {
        const delay = RETRY_DELAYS_MS[attempt] ?? 1000;
        await new Promise((r) => setTimeout(r, delay));
      } else {
        throw err;
      }
    }
  }
}

/**
 * Mise à jour en lot des étapes (une requête, une transaction côté serveur)
 * @param {string} beneficiaireId
 * @param {Array<{ etapeId: string, document_statut?: string, dependency_status?: object }>} updates
 * @returns {Promise<{ success: boolean, data: { updated: array, failed: array } }>}
 */
export async function updateEtapesBatch(beneficiaireId, updates) {
  const response = await api.put(`/parcours/${beneficiaireId}/etapes`, { updates });
  return response.data;
}

/**
 * Same as updateEtapesBatch but retries on 5xx/network (transient failures).
 */
export async function updateEtapesBatchWithRetry(beneficiaireId, updates, options = {}) {
  const { maxRetries = 2 } = options;
  const delays = [500, 1000];
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await updateEtapesBatch(beneficiaireId, updates);
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      const retryable = (status != null && status >= 500) || (status == null && err.message && /network|econnreset|timeout|failed/i.test(err.message));
      if (attempt < maxRetries && retryable) {
        await new Promise((r) => setTimeout(r, delays[attempt] ?? 1000));
      } else {
        throw err;
      }
    }
  }
  throw lastErr;
}

/**
 * Créer la phase d'inscription (Inscrit Sur La Liste, Carte Retirée)
 * @param {string} beneficiaireId
 * @param {boolean} sameCommune - true = CAS-E2 (même commune), false = CAS-E1
 */
export async function createInscriptionPhase(beneficiaireId, sameCommune) {
  const response = await api.post(`/parcours/${beneficiaireId}/inscription-phase`, { sameCommune });
  return response.data;
}
